import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BiSearch, BiFilterAlt } from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls";

const ServerCasesList = () => {
  const token = localStorage.getItem("token");

  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [casesPerPage] = useState(10);

  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const fetchCases = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/server/my-cases`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCases(res.data.data || []);
    } catch (err) {
      console.error("Error fetching cases:", err);
      setError("Failed to load cases.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, []);

  // helper: normalize status strings for comparisons (handles "in progress" vs "in_progress")
  const normalize = (v) =>
    (v || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/-+/g, "_");

  const filteredCases = cases.filter((c) => {
    const searchText = search.toLowerCase().trim();
    const matchesSearch =
      !searchText ||
      c.case_name?.toLowerCase().includes(searchText) ||
      c.client_name?.toLowerCase().includes(searchText);
    const matchesStatus = !filterStatus || normalize(c.server_work_status) === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const indexOfLastCase = currentPage * casesPerPage;
  const indexOfFirstCase = indexOfLastCase - casesPerPage;
  const currentCases = filteredCases.slice(indexOfFirstCase, indexOfLastCase);
  const totalPages = Math.ceil(filteredCases.length / casesPerPage);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    // optional: scroll to top of list on page change
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatLabel = (value) => {
    if (!value) return "";
    return value
      .toString()
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getStatusColor = (value) => {
    const v = (value || "").toString().toLowerCase();
    if (["completed", "served", "closed"].includes(v)) return "bg-green-100 text-green-700";
    if (["in progress", "in_progress", "attempted"].includes(v)) return "bg-blue-100 text-blue-700";
    if (["pending", "assigned"].includes(v)) return "bg-yellow-100 text-yellow-700";
    if (["failed", "cancelled", "cancelled", "returned"].includes(v)) return "bg-red-100 text-red-700";
    return "bg-gray-100 text-gray-700";
  };

  if (loading) return <div className="p-6 text-center">Loading cases...</div>;
  if (error) return <div className="p-6 text-center text-red-500">{error}</div>;

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">My Assigned Cases</h1>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 w-full sm:w-1/3 border rounded px-2 bg-white">
          <BiSearch className="text-gray-500" />
          <input
            type="text"
            placeholder="Search by case or client..."
            className="w-full p-2 focus:outline-none bg-transparent text-sm"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-1/4 border rounded px-2 bg-white">
          <BiFilterAlt className="text-gray-500" />
          <select
            className="w-full p-2 focus:outline-none bg-transparent text-sm"
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="finalized">Finalized</option>
          </select>
        </div>
      </div>

      {/* MOBILE: Card list */}
      <div className="block sm:hidden space-y-3">
        {currentCases.length > 0 ? (
          currentCases.map((c) => (
            <Link
              to={`/server/cases/${c.id}`}
              key={c.id}
              className="group block bg-white rounded-lg shadow-sm hover:shadow-md border border-gray-200 p-4 transition-transform transform hover:-translate-y-0.5"
            >
              <div className="flex justify-between items-start gap-3">
                {/* Left: title + client (flex column) */}
                <div className="min-w-0 flex-1">
                  <p className="text-md font-semibold text-gray-800 truncate">{c.case_name || "Untitled Case"}</p>
                  <p className="text-sm text-gray-500 mt-1 truncate">Client: {c.client_name || "N/A"}</p>
                  <div className="flex-shrink-0 flex flex-col">
                    <p className="text-xs text-gray-500 mt-2">
                      Service Status:                     <span
                      className={`inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                        c.server_work_status
                      )}`}
                      style={{ minWidth: 64 }}
                    >
                    {formatLabel(c.server_work_status)}
                  </span>
                    </p>


                </div>
              </div>

              {/* Right: status, due date, view */}
              <div className="flex-shrink-0 flex flex-col items-end text-right">
                <span
                  className={`inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                    c.status
                  )}`}
                  style={{ minWidth: 64 }}
                >
                  {formatLabel(c.status)}
                </span>

                <p className="text-xs text-gray-500 mt-2">
                  Due: {c.due_date ? new Date(c.due_date).toLocaleDateString() : "N/A"}
                </p>

                <span className="text-xs text-blue-600 font-medium mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  View
                </span>
              </div>
            </div>
            </Link>
      ))
      ) : (
      <div className="py-6 text-center text-gray-500">No cases found.</div>
        )}
    </div>

      {/* DESKTOP: Table */ }
  <div className="hidden sm:block overflow-x-auto bg-white rounded-lg shadow-sm">
    <table className="min-w-full divide-y divide-gray-200 text-sm">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Case Name
          </th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Client
          </th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Case Status
          </th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Service Status
          </th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Due Date
          </th>
        </tr>
      </thead>

      <tbody className="bg-white divide-y divide-gray-200">
        {currentCases.length > 0 ? (
          currentCases.map((c) => (
            <tr key={c.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 break-words">
                <Link to={`/server/cases/${c.id}`} className="text-blue-600 hover:underline">
                  {c.case_name || "Untitled Case"}
                </Link>
              </td>

              <td className="px-4 py-3 break-words text-gray-600">{c.client_name || "N/A"}</td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(c.status)}`}>
                  {formatLabel(c.status)}
                </span>
              </td>

              <td className="px-4 py-3">
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(c.server_work_status)}`}>
                  {formatLabel(c.server_work_status)}
                </span>
              </td>

              <td className="px-4 py-3 text-gray-600">
                {c.due_date ? new Date(c.due_date).toLocaleDateString() : "N/A"}
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="4" className="px-4 py-4 text-center text-gray-500">
              No cases found.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>

  {/* Pagination */ }
  {
    totalPages > 1 && (
      <div className="flex flex-wrap justify-center items-center gap-2 mt-4">
        <button
          onClick={() => goToPage(currentPage - 1)}
          className="px-3 py-1 border rounded disabled:opacity-50"
          disabled={currentPage === 1}
        >
          Prev
        </button>

        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            onClick={() => goToPage(page)}
            className={`px-3 py-1 border rounded ${page === currentPage ? "bg-blue-600 text-white" : ""}`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => goToPage(currentPage + 1)}
          className="px-3 py-1 border rounded disabled:opacity-50"
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    )
  }
    </div >
  );
};

export default ServerCasesList;
